import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertTriangle, XOctagon, ArrowLeft, RefreshCw } from "lucide-react";
import DocsNavigation from "../components/landing/DocsNavigation";
import { Footer } from "../components/landing/Footer";

const POLL_INTERVAL_MS = 60 * 1000;

const StatusBar = ({ days }) => (
  <div
    className="flex items-end gap-[2px] w-full h-9"
    role="img"
    aria-label="90 day uptime"
  >
    {days.map((d) => {
      const color =
        d.status === "down"
          ? "bg-rose-400/80 hover:bg-rose-300"
          : d.status === "degraded"
          ? "bg-amber-400/80 hover:bg-amber-300"
          : d.status === "paused" || d.status === "not_checked"
          ? "bg-gray-500/40 hover:bg-gray-400"
          : "bg-emerald-400/70 hover:bg-emerald-300";
      const tip =
        d.uptime_pct !== undefined
          ? `${d.date} — ${d.uptime_pct.toFixed(2)}% uptime`
          : `${d.date} — ${d.status}`;
      return (
        <div
          key={d.date}
          title={tip}
          className={`flex-1 min-w-[2px] h-full rounded-[1.5px] cursor-default transition-colors ${color}`}
        />
      );
    })}
  </div>
);

const StatusBadge = ({ status }) => {
  if (status === "down") {
    return (
      <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-rose-300 font-semibold">
        <XOctagon size={12} /> Down
      </span>
    );
  }
  if (status === "degraded") {
    return (
      <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-amber-300 font-semibold">
        <AlertTriangle size={12} /> Degraded
      </span>
    );
  }
  if (status === "paused" || status === "not_checked") {
    return (
      <span className="text-xs uppercase tracking-wider text-gray-400 font-semibold">
        Paused
      </span>
    );
  }
  return (
    <span className="text-xs uppercase tracking-wider text-emerald-300 font-semibold">
      Operational
    </span>
  );
};

const StatusPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const res = await axios.get(`${apiBase}/api/utils/uptime`);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError("Failed to load status. Retrying…");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const wasLight = html.classList.contains("light");
    if (wasLight) {
      html.classList.remove("light");
      html.classList.add("dark");
    }
    window.scrollTo(0, 0);
    return () => {
      if (wasLight) {
        html.classList.remove("dark");
        html.classList.add("light");
      }
    };
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  const allOperational = data?.all_operational !== false;
  const lastUpdated = data?.last_updated
    ? new Date(data.last_updated).toUTCString()
    : new Date().toUTCString();
  const services = data?.services || [];

  return (
    <div
      className="min-h-screen bg-space-900 relative"
      data-testid="status-page"
    >
      <DocsNavigation />

      <main className="relative pt-28 pb-24 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <Link
            to="/support"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-teal-400 transition-colors mb-8"
            data-testid="status-back-link"
          >
            <ArrowLeft size={16} />
            Back to Support
          </Link>

          {/* Global banner */}
          <section
            className={`p-8 rounded-2xl border ${
              allOperational
                ? "bg-emerald-400/[0.06] border-emerald-400/30"
                : "bg-amber-400/[0.06] border-amber-400/30"
            } mb-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4`}
            data-testid="status-banner"
          >
            <div className="flex items-center gap-4">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center border ${
                  allOperational
                    ? "bg-emerald-400/15 border-emerald-400/30"
                    : "bg-amber-400/15 border-amber-400/30"
                }`}
              >
                {allOperational ? (
                  <CheckCircle2 className="text-emerald-300" size={26} />
                ) : (
                  <AlertTriangle className="text-amber-300" size={26} />
                )}
              </div>
              <div>
                <h1 className="font-display font-semibold text-2xl md:text-3xl text-white">
                  {loading
                    ? "Checking services…"
                    : allOperational
                    ? "All services operational"
                    : "Partial service degradation"}
                </h1>
                <p className="text-sm text-gray-400 mt-1">
                  Last updated {lastUpdated}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={load}
                className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-teal-300 transition-colors"
                data-testid="status-refresh"
              >
                <RefreshCw size={14} /> Refresh
              </button>
              <Link
                to="/support"
                className="px-5 py-2.5 rounded-full bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.1] text-white text-sm font-medium transition-colors"
                data-testid="status-report-issue"
              >
                Report an issue
              </Link>
            </div>
          </section>

          {error && (
            <div className="mb-6 px-4 py-3 rounded-xl border border-rose-400/30 bg-rose-400/[0.06] text-rose-200 text-sm">
              {error}
            </div>
          )}

          {/* Services */}
          <section className="space-y-4">
            {services.map((svc) => (
              <div
                key={svc.id}
                className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06]"
                data-testid={`status-service-${svc.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          svc.status === "down"
                            ? "bg-rose-400"
                            : svc.status === "degraded"
                            ? "bg-amber-400"
                            : "bg-emerald-400"
                        }`}
                      />
                      <h3 className="text-white font-semibold">{svc.name}</h3>
                    </div>
                    {svc.description && (
                      <p className="text-sm text-gray-400 mt-1 ml-4">
                        {svc.description}
                      </p>
                    )}
                  </div>
                  <div className="text-right ml-4">
                    <StatusBadge status={svc.status} />
                    <div className="text-xs text-gray-500 mt-0.5">
                      {svc.uptime_90d}% uptime · 90d
                    </div>
                  </div>
                </div>
                <StatusBar days={svc.days || []} />
                <div className="flex items-center justify-between text-[10px] text-gray-500 mt-2 uppercase tracking-wider">
                  <span>90 days ago</span>
                  <span>Today</span>
                </div>
              </div>
            ))}
          </section>

          {/* Incident history */}
          <section className="mt-12">
            <h2 className="font-display font-semibold text-xl text-white mb-4">
              Recent incidents
            </h2>
            <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06] text-sm text-gray-400">
              {(data?.incidents || []).length === 0 ? (
                <>No incidents reported in the last 90 days.</>
              ) : (
                <ul className="space-y-3">
                  {data.incidents.map((inc, i) => (
                    <li key={i}>
                      <div className="text-white font-medium">{inc.title}</div>
                      <div className="text-xs text-gray-500">{inc.date}</div>
                      {inc.summary && (
                        <div className="text-sm text-gray-400 mt-1">
                          {inc.summary}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <p className="text-xs text-gray-500 text-center mt-12">
            Subscribe to incident notifications by emailing{" "}
            <a
              href="mailto:support@voxinity.ai?subject=Subscribe to status alerts"
              className="text-teal-300 hover:underline"
            >
              support@voxinity.ai
            </a>
            . Status refreshes automatically every 60 seconds.
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StatusPage;
